import { useEffect, useRef, useState } from 'react';
import { getActiveJobs } from '../api';
import type { ActiveJob } from '../api';

const ACTIVE_POLL_INTERVAL_MS = 3000;
const IDLE_POLL_INTERVAL_MS = 30000;

/**
 * Polls active jobs quickly while work is running, and much less frequently
 * when idle. Polling pauses in hidden tabs and resumes on visibility restore.
 */
export function useActiveJobs(workspaceId: number) {
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleNext = (delayMs: number, run: () => void) => {
    clearTimer();
    timerRef.current = setTimeout(run, delayMs);
  };

  const fetchJobs = async () => {
    if (document.hidden || inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const res = await getActiveJobs(workspaceId);
      const jobs = res.jobs ?? [];
      setActiveJobs(jobs);
      scheduleNext(jobs.length > 0 ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS, () => {
        void fetchJobs();
      });
    } catch {
      // Silently ignore network errors; don't disrupt the app.
      scheduleNext(IDLE_POLL_INTERVAL_MS, () => {
        void fetchJobs();
      });
    } finally {
      inFlightRef.current = false;
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimer();
        return;
      }
      void fetchJobs();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (!document.hidden) {
      void fetchJobs();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimer();
      inFlightRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  return { activeJobs };
}
